export interface RoomLink {
  roomId: string;
  /** Null when the link does not name a signalling server. */
  signalingUrl: string | null;
  name: string | null;
  /** Present only in guest links: the invite the server checks before letting a guest in. */
  inviteToken: string | null;
  /** The host only uses relayed (TURN) candidates; this end must do the same to connect. */
  relay: boolean;
}

export interface RoomUrlOptions {
  /** Adds `relay=1` to both links, for a host running with `iceTransportPolicy: 'relay'`. */
  relay?: boolean;
}

const ROOM_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{22,64}$/;

/** The fragment keeps the room id out of HTTP requests, server logs and Referer headers. */
export function formatRoomHash(
  roomId: string,
  signalingUrl: string,
  inviteToken?: string,
  options: RoomUrlOptions = {},
): string {
  let hash = `#room=${encodeURIComponent(roomId)}&signal=${encodeURIComponent(signalingUrl)}`;
  if (inviteToken !== undefined) hash += `&invite=${encodeURIComponent(inviteToken)}`;
  if (options.relay) hash += '&relay=1';
  return hash;
}

export function parseRoomHash(hash: string): RoomLink | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const roomId = params.get('room');
  if (roomId === null || !ROOM_ID_PATTERN.test(roomId)) return null;
  const signal = params.get('signal');
  const name = params.get('name');
  const invite = params.get('invite');
  return {
    roomId,
    signalingUrl: signal !== null && isSignalingUrl(signal) ? signal : null,
    name: name !== null && name.trim() !== '' ? name.trim().slice(0, 64) : null,
    inviteToken: invite !== null && TOKEN_PATTERN.test(invite) ? invite : null,
    relay: params.get('relay') === '1',
  };
}

export function isSignalingUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'ws:' || url.protocol === 'wss:';
  } catch {
    return false;
  }
}

/** Signalling server assumed when a link omits it: same host as the page, default port. */
export function defaultSignalingUrl(page: { protocol: string; hostname: string }): string {
  return `${page.protocol === 'https:' ? 'wss' : 'ws'}://${page.hostname}:8787`;
}

/**
 * Whether a page may connect to the signalling server a link names. A link is data anyone
 * can write; trusting its `signal=` would let a crafted link on this origin, with this
 * origin's camera permission, stream a guest to an attacker's server. Accepted: the page's own
 * host (any port), and origins the deployment lists in `VITE_SIGNALING_ORIGINS` at build time.
 * From an https page only wss: is accepted.
 */
export function signalingUrlProblem(
  url: string,
  page: { protocol: string; hostname: string },
  allowedOrigins: readonly string[],
): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'The link does not contain a valid signalling server address.';
  }
  if (parsed.protocol !== 'ws:' && parsed.protocol !== 'wss:') return 'The signalling address is not a WebSocket URL.';
  if (page.protocol === 'https:' && parsed.protocol !== 'wss:') {
    return 'This secure page cannot use an unencrypted (ws:) signalling server.';
  }
  const sameHost = parsed.hostname.toLowerCase() === page.hostname.toLowerCase();
  const listed = allowedOrigins.some((origin) => origin.trim().replace(/\/+$/, '').toLowerCase() === parsed.origin.toLowerCase());
  if (!sameHost && !listed) return `This link points to a signalling server this site does not trust (${parsed.host}).`;
  return null;
}

/** Origins listed at build time, e.g. `VITE_SIGNALING_ORIGINS=wss://signal.example.com,wss://backup.example.com`. */
export function configuredSignalingOrigins(): string[] {
  const raw: unknown = import.meta.env.VITE_SIGNALING_ORIGINS;
  return typeof raw === 'string' ? raw.split(',').map((o) => o.trim()).filter((o) => o !== '') : [];
}

/** `appBase` is the absolute URL of the directory that holds viewer.html and guest.html. */
export function buildRoomUrlsFrom(
  appBase: string,
  roomId: string,
  signalingUrl: string,
  inviteToken?: string,
  options: RoomUrlOptions = {},
): { viewerUrl: string; guestUrl: string } {
  return {
    // The viewer link must never carry an invite: that is what keeps it view-only.
    viewerUrl: new URL('viewer.html', appBase).href + formatRoomHash(roomId, signalingUrl, undefined, options),
    guestUrl:
      inviteToken === undefined || inviteToken === ''
        ? ''
        : new URL('guest.html', appBase).href + formatRoomHash(roomId, signalingUrl, inviteToken, options),
  };
}

export function buildRoomUrls(
  roomId: string,
  signalingUrl: string,
  inviteToken?: string,
  options: RoomUrlOptions = {},
): { viewerUrl: string; guestUrl: string } {
  // BASE_URL may be relative ('./'), so resolve it against the page rather than the bare origin.
  const appBase = new URL(import.meta.env.BASE_URL, new URL(location.pathname, location.origin)).href;
  return buildRoomUrlsFrom(appBase, roomId, signalingUrl, inviteToken, options);
}
